/** Subscription fields used for checkout vs entitled gating (#375, #382, #389). */

export type SubCheckoutContext = {
  subscription_tier?: string;
  had_trial?: boolean;
  has_payment_method?: boolean;
  subscribe_via_checkout?: boolean;
  resubscribe_via_checkout?: boolean;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  has_stripe_subscription?: boolean;
  trial_ends_at?: string | null;
};

/**
 * Day-0 limbo: DB tier still `trial` but wall-clock is past `trial_ends_at` (cron not flipped yet).
 * API: `trial_days_remaining: 0`, `resubscribe_via_checkout: false`, `subscribe_via_checkout: true`.
 */
export function isDay0TrialLimbo(
  ctx: SubCheckoutContext | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (!ctx || ctx.subscription_tier !== 'trial' || !ctx.trial_ends_at) return false;
  const endMs = new Date(ctx.trial_ends_at).getTime();
  return !Number.isNaN(endMs) && endMs < nowMs;
}

/**
 * Day-1 expired: DB tier `expired`, or `none` with prior trial (returning lapsed user).
 * API: `resubscribe_via_checkout: true`, `subscribe_via_checkout: true`.
 */
export function isDay1ExpiredTier(ctx: SubCheckoutContext | null | undefined): boolean {
  if (!ctx) return false;
  const tier = ctx.subscription_tier;
  if (tier === 'expired') return true;
  return tier === 'none' && !!ctx.had_trial;
}

/**
 * Playback/content gate — NOT used to block billing surfaces (Settings, /subscribe, account).
 * Day-0 limbo and day-1 expired both return true so routing stays aligned (#389).
 */
export function shouldBlockAccess(
  data: SubCheckoutContext | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (!data) return false;
  const tier = data.subscription_tier;
  if (tier === 'pending_checkout') return true;
  if (tier === 'expired' && !data.had_trial && !data.stripe_customer_id) return true;
  const activeTiers = ['active', 'trial', 'grace', 'beta', 'cancelling'];
  if (activeTiers.includes(tier ?? '')) {
    if (tier === 'trial' && data.trial_ends_at) {
      return new Date(data.trial_ends_at).getTime() < nowMs;
    }
    return false;
  }
  return tier === 'expired' || (tier === 'none' && !!data.had_trial);
}

/** Routes where billing/subscribe/account must remain reachable regardless of entitlement (#389). */
export function isBillingSurfacePath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, '') || '/';
  return p === '/subscribe'
    || p.startsWith('/subscribe/')
    || p === '/manage-subscription'
    || p === '/billing'
    || p === '/login'
    || p === '/welcome-back'
    || p === '/app/account'
    || p.endsWith('/account');
}

const PAID_ENTITLED_TIERS = new Set(['active', 'grace', 'beta', 'cancelling']);

/** True when the user has a live Stripe subscription (not grant/comp-only entitlement). */
export function hasStripeSubscription(
  ctx: SubCheckoutContext | null | undefined,
): boolean {
  if (!ctx) return false;
  if (ctx.has_stripe_subscription === true) return true;
  if (ctx.stripe_subscription_id) return true;
  if (!ctx.stripe_customer_id) return false;
  if (ctx.subscribe_via_checkout || ctx.resubscribe_via_checkout) return false;
  return !!ctx.has_payment_method;
}

/** True when the user must complete Stripe checkout before they are a paid subscriber. */
export function stillNeedsPaidCheckout(ctx: SubCheckoutContext | null | undefined): boolean {
  if (!ctx) return true;
  if (hasStripeSubscription(ctx)) return false;
  if (ctx.subscribe_via_checkout || ctx.resubscribe_via_checkout) return true;
  const tier = ctx.subscription_tier;
  if (tier === 'expired' || tier === 'none' || tier === 'pending_checkout') return true;
  if (tier === 'trial') return true;
  if (isPaidEntitledTier(tier)) return true;
  return false;
}

/**
 * Post-trial / expired focused resubscribe shell (#363, #375).
 * had_trial alone is NOT sufficient — every desktop trial signup sets had_trial=true.
 */
export function isReturningSubscriberCheckout(
  resubscribeMode: boolean,
  subContext: SubCheckoutContext | null | undefined,
): boolean {
  if (resubscribeMode) return true;
  if (!subContext) return false;
  const tier = subContext.subscription_tier;
  if (tier === 'expired' || tier === 'none') return true;
  if (subContext.resubscribe_via_checkout) return true;
  return false;
}

/** Show already-subscribed UI only when Stripe billing exists and checkout is not required. */
export function shouldShowAlreadySubscribed(
  ctx: SubCheckoutContext | null | undefined,
): boolean {
  return hasStripeSubscription(ctx) && !stillNeedsPaidCheckout(ctx);
}

/** API already_subscribed must not block when entitlement still requires checkout. */
export function shouldIgnoreAlreadySubscribed(
  ctx: SubCheckoutContext | null | undefined,
): boolean {
  return stillNeedsPaidCheckout(ctx);
}

/** Manage opens Stripe portal only when a Stripe subscription exists; otherwise checkout. */
export function shouldRouteManageToCheckout(
  ctx: SubCheckoutContext | null | undefined,
): boolean {
  return stillNeedsPaidCheckout(ctx) || !hasStripeSubscription(ctx);
}

export function isPaidEntitledTier(tier: string | null | undefined): boolean {
  return !!tier && PAID_ENTITLED_TIERS.has(tier);
}
